// src/pages/Usuarios.js
import React, { useState, useEffect } from "react";
import supabase from "../supabaseClient";
import { useTenant } from "../context/TenantContext";
import useLimites from "../hooks/useLimites";
import Swal from "sweetalert2";
import Protegido from "../components/Protegido";
import { MODULOS_BLOQUEABLES, getModulosBloqueados } from "../config/modulos";

const API_URL = process.env.REACT_APP_API_URL;

export default function Usuarios() {
  // esAdmin viene del TenantContext, que sí contempla admin Y super_admin.
  // Antes se redefinía aquí como rol === "admin", y eso dejaba al super_admin
  // sin la pestaña "Mi Equipo" en su propia empresa.
  const { tenant, perfil, esAdmin } = useTenant();
  const { puedeAgregarUsuario, mensajeBloqueo, conteos, limites, recargarConteos } = useLimites();

  // ─── Equipo (admin) ──────────────────────────────────────────
  // Mi Perfil vive ahora en src/pages/MiPerfil.js (ruta /mi-perfil).
  // Esta página quedó SOLO para gestión de equipo, y por eso puede
  // seguir con <Protegido soloAdmin> sin dejar a un empleado sin forma
  // de cambiar su contraseña.
  const [usuarios, setUsuarios] = useState([]);
  const [mostrarFormCrear, setMostrarFormCrear] = useState(false);
  const [formNuevo, setFormNuevo] = useState({
    nombre: "",
    email: "",
    password: "",
    rol: "empleado",
  });
  const [cargando, setCargando] = useState(false);

  // ─── Permisos por empleado ───────────────────────────────────
  const [usuarioPermisos, setUsuarioPermisos] = useState(null); // empleado en edición
  const [bloqueados, setBloqueados] = useState([]);             // ids bloqueados
  const [guardandoPermisos, setGuardandoPermisos] = useState(false);

  // ─── Cargar datos ────────────────────────────────────────────
  useEffect(() => {
    if (esAdmin && tenant?.id) cargarUsuarios();
  }, [esAdmin, tenant?.id]);

  const cargarUsuarios = async () => {
    // Solo usuarios no borrados (deleted_at IS NULL)
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("tenant_id", tenant.id)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });

    if (error) console.error("Error cargando usuarios:", error);
    else setUsuarios(data || []);
  };

  // ─── Crear empleado (admin) ──────────────────────────────────
  const crearEmpleado = async () => {
    const { nombre, email, password, rol } = formNuevo;

    if (!nombre.trim() || !email.trim() || !password) {
      return Swal.fire("Campos requeridos", "Nombre, email y contraseña son obligatorios", "warning");
    }
    // Misma política que Register.js y MiPerfil.js
    if (password.length < 8) {
      return Swal.fire("Contraseña corta", "Mínimo 8 caracteres", "warning");
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      return Swal.fire("Contraseña insegura", "Debe incluir mayúscula, minúscula y número", "warning");
    }
    if (!email.includes("@")) {
      return Swal.fire("Email inválido", "Ingresa un email válido", "warning");
    }

    // Verificar límite de usuarios
    if (!puedeAgregarUsuario()) {
      const msg = mensajeBloqueo("usuario");
      return Swal.fire(msg.titulo, msg.mensaje, msg.icono);
    }

    try {
      setCargando(true);

      // Obtener token de sesión actual
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setCargando(false);
        return Swal.fire("Error", "No hay sesión activa", "error");
      }

      const res = await fetch(`${API_URL}/api/empleados/crear`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ nombre: nombre.trim(), email: email.trim().toLowerCase(), password, rol }),
      });

      const data = await res.json();

      if (!res.ok) {
        setCargando(false);
        return Swal.fire("Error", data.error || "No se pudo crear el usuario", "error");
      }

      Swal.fire("✅ Usuario creado", `${nombre} ahora puede acceder al sistema`, "success");
      setFormNuevo({ nombre: "", email: "", password: "", rol: "empleado" });
      setMostrarFormCrear(false);
      cargarUsuarios();
      recargarConteos();
    } catch (err) {
      console.error("Error creando empleado:", err);
      Swal.fire("Error de conexión", "No se pudo conectar al servidor", "error");
    } finally {
      setCargando(false);
    }
  };

  // ─── Cambiar estado de usuario (activar/desactivar) ──────────
  const toggleActivo = async (usuario) => {
    if (usuario.id === perfil.id) {
      return Swal.fire("No permitido", "No puedes desactivar tu propia cuenta", "warning");
    }

    const nuevoEstado = !usuario.activo;
    const accion = nuevoEstado ? "activar" : "desactivar";

    const confirmar = await Swal.fire({
      title: `¿${nuevoEstado ? "Activar" : "Desactivar"} a ${usuario.nombre}?`,
      text: nuevoEstado
        ? "El usuario podrá acceder al sistema nuevamente."
        : "El usuario no podrá iniciar sesión hasta que lo reactives.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: nuevoEstado ? "#22c55e" : "#ef4444",
    });

    if (!confirmar.isConfirmed) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${API_URL}/api/usuarios/${usuario.id}/estado`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ activo: nuevoEstado }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        return Swal.fire("Error", data.error || "No se pudo actualizar el usuario", "error");
      }
      Swal.fire("✅ Listo", `${usuario.nombre} fue ${accion === "activar" ? "activado" : "desactivado"}`, "success");
      cargarUsuarios();
    } catch (err) {
      console.error("Error toggle activo:", err);
      Swal.fire("Error de conexión", "Verifica tu internet e intenta de nuevo", "error");
    }
  };

  // ─── Borrar usuario (soft delete con deleted_at) ─────────────
  const borrarUsuario = async (usuario) => {
    if (usuario.id === perfil.id) {
      return Swal.fire("No permitido", "No puedes borrar tu propia cuenta", "warning");
    }

    const confirmar = await Swal.fire({
      title: `¿Borrar a ${usuario.nombre}?`,
      html: `Esta acción es <strong>permanente</strong>. El usuario no podrá iniciar sesión y dejará de aparecer en la lista.<br><br>Sus pedidos y movimientos creados se conservan en el historial.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, borrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmar.isConfirmed) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${API_URL}/api/usuarios/${usuario.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        return Swal.fire("Error", data.error || "No se pudo borrar el usuario", "error");
      }
      Swal.fire("✅ Borrado", `${usuario.nombre} fue eliminado`, "success");
      cargarUsuarios();
      recargarConteos();
    } catch (err) {
      console.error("Error borrando usuario:", err);
      Swal.fire("Error de conexión", "Verifica tu internet e intenta de nuevo", "error");
    }
  };

  // ─── Cambiar rol de usuario ──────────────────────────────────
  // Va por el backend con service_role. El navegador ya no tiene permiso
  // de escribir profiles.rol; las reglas anti-escalada viven en el servidor.
  const cambiarRol = async (usuario, nuevoRol) => {
    if (usuario.id === perfil.id) {
      return Swal.fire("No permitido", "No puedes cambiar tu propio rol", "warning");
    }

    const confirmar = await Swal.fire({
      title: `¿Cambiar el rol de ${usuario.nombre}?`,
      html:
        nuevoRol === "admin"
          ? "Como <strong>Administrador</strong> podrá gestionar usuarios y los datos de la empresa."
          : "Como <strong>Empleado</strong> dejará de gestionar usuarios y datos de la empresa.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, cambiar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmar.isConfirmed) {
      cargarUsuarios(); // devuelve el <select> a su valor real
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${API_URL}/api/empleados/${usuario.id}/rol`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ rol: nuevoRol }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        cargarUsuarios();
        return Swal.fire("Error", data.error || "No se pudo cambiar el rol", "error");
      }

      Swal.fire("✅ Rol actualizado", data.mensaje, "success");
      cargarUsuarios();
    } catch (err) {
      console.error("Error cambiando rol:", err);
      cargarUsuarios();
      Swal.fire("Error de conexión", "Verifica tu internet e intenta de nuevo", "error");
    }
  };

  // ─── Permisos por módulo ─────────────────────────────────────
  const abrirPermisos = (usuario) => {
    setUsuarioPermisos(usuario);
    setBloqueados(getModulosBloqueados(usuario));
  };

  // El checkbox representa "tiene acceso". Marcarlo = quitarlo de bloqueados.
  const alternarModulo = (moduloId) => {
    setBloqueados((prev) =>
      prev.includes(moduloId) ? prev.filter((m) => m !== moduloId) : [...prev, moduloId]
    );
  };

  const guardarPermisos = async () => {
    if (guardandoPermisos) return;
    setGuardandoPermisos(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${API_URL}/api/empleados/${usuarioPermisos.id}/permisos`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ modulos_bloqueados: bloqueados }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        return Swal.fire("Error", data.error || "No se pudieron guardar los permisos", "error");
      }

      setUsuarioPermisos(null);
      Swal.fire("✅ Permisos guardados", data.mensaje, "success");
      cargarUsuarios();
    } catch (err) {
      console.error("Error guardando permisos:", err);
      Swal.fire("Error de conexión", "Verifica tu internet e intenta de nuevo", "error");
    } finally {
      setGuardandoPermisos(false);
    }
  };

  // ─── Estilos ─────────────────────────────────────────────────
  const estilos = {
    container: { padding: "1rem", maxWidth: "700px", margin: "auto" },
    titulo: { textAlign: "center", fontSize: "clamp(1.5rem, 4vw, 2rem)", marginBottom: 8 },
    subtitulo: { textAlign: "center", fontSize: 14, color: "#9ca3af", marginBottom: 24 },
    card: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 20,
      marginBottom: 16,
      background: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 },
    input: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      boxSizing: "border-box",
      marginBottom: 12,
    },
    select: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      boxSizing: "border-box",
      marginBottom: 12,
      background: "#fff",
    },
    btnPrimario: {
      width: "100%",
      padding: 12,
      background: "#0077B6",
      color: "white",
      border: "none",
      borderRadius: 8,
      fontSize: 15,
      fontWeight: 600,
      cursor: "pointer",
    },
    btnSecundario: {
      width: "100%",
      padding: 10,
      background: "#f3f4f6",
      color: "#374151",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      cursor: "pointer",
      marginTop: 8,
    },
    btnAgregar: {
      padding: "10px 20px",
      background: "#00B4D8",
      color: "white",
      border: "none",
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      marginBottom: 16,
    },
    userItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 16px",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      marginBottom: 8,
      background: "#fff",
    },
    badge: (tipo) => ({
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      background: tipo === "admin" ? "#dbeafe" : "#f3e8ff",
      color: tipo === "admin" ? "#1d4ed8" : "#7c3aed",
    }),
    badgeActivo: (activo) => ({
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: activo ? "#22c55e" : "#ef4444",
      marginRight: 8,
    }),
    contador: {
      textAlign: "center",
      fontSize: 13,
      color: "#9ca3af",
      marginBottom: 16,
    },
    btnAccion: (color) => ({
      padding: "6px 10px",
      borderRadius: 6,
      border: "none",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      background: color === "verde" ? "#dcfce7"
                : color === "rojo" ? "#fee2e2"
                : color === "borrar" ? "#fef2f2"
                : "#f3f4f6",
      color: color === "verde" ? "#16a34a"
           : color === "rojo" ? "#dc2626"
           : color === "borrar" ? "#991b1b"
           : "#374151",
      border: color === "borrar" ? "1px solid #fca5a5" : "none",
    }),
  };

  return (
    <Protegido soloAdmin>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 700 }}>
          <div className="sw-header">
            <h1 className="sw-header-titulo">👤 Usuarios</h1>
          </div>
          <p style={{ textAlign: "center", color: "#9ca3af", marginBottom: 24, marginTop: -10 }}>
            {tenant?.nombre || "Cargando..."}
          </p>

      {esAdmin && (
          <>
            <p style={estilos.contador}>
              {conteos.usuarios} de {limites.maxUsuarios === Infinity ? "ilimitados" : limites.maxUsuarios} usuarios
            </p>

            {/* Botón agregar */}
            {!mostrarFormCrear && (
              <button onClick={() => setMostrarFormCrear(true)} style={estilos.btnAgregar}>
                ➕ Agregar usuario
              </button>
            )}

            {/* ─── Formulario crear usuario ───────────────── */}
            {mostrarFormCrear && (
              <div style={estilos.card}>
                <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>Nuevo usuario</h3>

                <label style={estilos.label}>Nombre completo</label>
                <input
                  type="text"
                  placeholder="Nombre del empleado"
                  value={formNuevo.nombre}
                  onChange={(e) => setFormNuevo({ ...formNuevo, nombre: e.target.value })}
                  style={estilos.input}
                />

                <label style={estilos.label}>Email</label>
                <input
                  type="email"
                  placeholder="email@ejemplo.com"
                  value={formNuevo.email}
                  onChange={(e) => setFormNuevo({ ...formNuevo, email: e.target.value })}
                  style={estilos.input}
                />

                <label style={estilos.label}>Contraseña</label>
                <input
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={formNuevo.password}
                  onChange={(e) => setFormNuevo({ ...formNuevo, password: e.target.value })}
                  style={estilos.input}
                />

                <label style={estilos.label}>Rol</label>
                <select
                  value={formNuevo.rol}
                  onChange={(e) => setFormNuevo({ ...formNuevo, rol: e.target.value })}
                  style={estilos.select}
                >
                  <option value="empleado">Empleado</option>
                  <option value="admin">Administrador</option>
                </select>

                <button
                  onClick={crearEmpleado}
                  disabled={cargando}
                  style={{
                    ...estilos.btnPrimario,
                    opacity: cargando ? 0.7 : 1,
                    cursor: cargando ? "not-allowed" : "pointer",
                  }}
                >
                  {cargando ? "Creando..." : "Crear usuario"}
                </button>

                <button
                  onClick={() => {
                    setMostrarFormCrear(false);
                    setFormNuevo({ nombre: "", email: "", password: "", rol: "empleado" });
                  }}
                  style={estilos.btnSecundario}
                >
                  Cancelar
                </button>
              </div>
            )}

            {/* ─── Lista de usuarios ──────────────────────── */}
            <h3 style={{ fontSize: 16, marginBottom: 12, marginTop: 8 }}>Usuarios de la empresa</h3>

            {usuarios.length === 0 ? (
              <p style={{ textAlign: "center", color: "#9ca3af" }}>No hay usuarios registrados</p>
            ) : (
              usuarios.map((u) => (
                <div key={u.id} style={estilos.userItem}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={estilos.badgeActivo(u.activo)} title={u.activo ? "Activo" : "Inactivo"} />
                      <strong style={{ fontSize: 15 }}>{u.nombre}</strong>
                      <span style={estilos.badge(u.rol)}>{u.rol}</span>
                      {u.id === perfil.id && (
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>(tú)</span>
                      )}
                    </div>
                    {u.email && (
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4, marginLeft: 16 }}>
                        📧 {u.email}
                      </div>
                    )}
                  </div>

                  {/* Acciones (no en sí mismo) */}
                  {u.id !== perfil.id && (
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <select
                        value={u.rol}
                        onChange={(e) => cambiarRol(u, e.target.value)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          fontSize: 13,
                        }}
                      >
                        <option value="empleado">Empleado</option>
                        <option value="admin">Admin</option>
                      </select>

                      <button
                        onClick={() => toggleActivo(u)}
                        title={u.activo ? "Desactivar usuario" : "Activar usuario"}
                        style={estilos.btnAccion(u.activo ? "rojo" : "verde")}
                      >
                        {u.activo ? "Desactivar" : "Activar"}
                      </button>

                      {/* Los permisos solo aplican a empleados: un admin gestiona
                          la empresa y limitarle módulos sería contradictorio. */}
                      {u.rol === "empleado" && (
                        <button
                          onClick={() => abrirPermisos(u)}
                          title="Elegir a qué módulos tiene acceso"
                          style={{
                            padding: "6px 12px",
                            borderRadius: 6,
                            border: "1px solid #c7d2fe",
                            background: "#eef2ff",
                            color: "#4338ca",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          🔑 Permisos
                        </button>
                      )}

                      <button
                        onClick={() => borrarUsuario(u)}
                        title="Borrar usuario (acción permanente)"
                        style={estilos.btnAccion("borrar")}
                      >
                        🗑️ Borrar
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* MODAL: PERMISOS POR MÓDULO                          */}
        {/* ═══════════════════════════════════════════════════ */}
        {usuarioPermisos && (
          <div
            onClick={() => !guardandoPermisos && setUsuarioPermisos(null)}
            style={{
              position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 16, zIndex: 1000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520,
                maxHeight: "85vh", overflowY: "auto", padding: 24,
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              }}
            >
              <h2 style={{ margin: "0 0 4px 0", fontSize: 20 }}>🔑 Permisos de acceso</h2>
              <p style={{ margin: "0 0 4px 0", fontSize: 15, fontWeight: 600, color: "#0077B6" }}>
                {usuarioPermisos.nombre}
              </p>
              <p style={{ margin: "0 0 20px 0", fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
                Marca los módulos a los que <strong>sí</strong> quieres que tenga acceso.
                Si desmarcas uno, desaparece de su menú y no podrá entrar aunque escriba
                la dirección a mano.
              </p>

              <div style={{
                background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8,
                padding: "10px 12px", fontSize: 12, color: "#166534", marginBottom: 18,
              }}>
                ✅ <strong>Inicio, Mi Perfil, Crear documento, Clientes, Inventario y
                Trazabilidad</strong> siempre están disponibles. Sin ellos, un empleado
                no podría trabajar.
              </div>

              {MODULOS_BLOQUEABLES.map((m) => {
                const tieneAcceso = !bloqueados.includes(m.id);
                return (
                  <div
                    key={m.id}
                    style={{
                      border: "1px solid #e5e7eb", borderRadius: 10,
                      padding: "12px 14px", marginBottom: 8,
                      background: tieneAcceso ? "#fff" : "#fffbeb",
                    }}
                  >
                    <label style={{
                      display: "flex", alignItems: "center", gap: 10,
                      cursor: "pointer", fontSize: 15,
                    }}>
                      <input
                        type="checkbox"
                        checked={tieneAcceso}
                        onChange={() => alternarModulo(m.id)}
                        style={{ width: 18, height: 18, cursor: "pointer", flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 18 }}>{m.icono}</span>
                      <span style={{ fontWeight: 600, color: "#111827" }}>{m.titulo}</span>
                    </label>

                    {/* El aviso solo aparece cuando de verdad lo va a perder */}
                    {!tieneAcceso && (
                      <div style={{
                        fontSize: 12, color: "#92400e", marginTop: 8,
                        marginLeft: 28, lineHeight: 1.5,
                      }}>
                        ⚠️ {m.aviso}
                      </div>
                    )}
                  </div>
                );
              })}

              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  onClick={() => setUsuarioPermisos(null)}
                  disabled={guardandoPermisos}
                  style={{
                    flex: 1, padding: 12, borderRadius: 8, fontSize: 15,
                    background: "#f3f4f6", color: "#374151",
                    border: "1px solid #d1d5db", cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarPermisos}
                  disabled={guardandoPermisos}
                  style={{
                    flex: 1, padding: 12, borderRadius: 8, fontSize: 15, fontWeight: 600,
                    background: guardandoPermisos ? "#93c5fd" : "#0077B6",
                    color: "#fff", border: "none",
                    cursor: guardandoPermisos ? "not-allowed" : "pointer",
                  }}
                >
                  {guardandoPermisos ? "Guardando..." : "Guardar permisos"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </Protegido>
  );
}